import { useId, useState } from 'react';
import type { NoteDiagnosis } from '@ehr/shared';
import { Modal } from './Modal.js';
import { addError, isIcd10, savedDiagnoses, type DxRow } from '../lib/noteDiagnosis.js';
import {
  initialPick,
  otherRows,
  pickerRows,
  selectedRows,
  setPrimary,
  tickCode,
  type DxPick,
} from '../lib/dxPicker.js';

/**
 * ehr#682 (PROGRESS_NOTE_10-20-V141 openDx), rebuilt for ehr#741 (Chase 10-31, V132 handoff
 * Part 1): the "Diagnosis" window that the Change link in Type, place and billing opens.
 * Every row is one grid (tick | Code | Diagnosis | Primary) under a sticky Code / Diagnosis /
 * Primary header. Ticked codes sit under "Selected (n)" at the top; the rest of every ICD-10 code
 * sits under "All diagnoses", the client's treatment plan codes first, then code order. Primary is
 * off until its code is ticked; the first ticked code is Primary. Nothing changes on the note until
 * Save; Cancel, the X or Escape leave it as it was.
 */
export function DiagnosisChangeModal({
  planDx,
  value,
  onSave,
  onClose,
}: {
  /** The client's diagnoses (their live treatment plan). */
  planDx: readonly { code: string; label: string }[];
  value: readonly NoteDiagnosis[];
  onSave: (next: NoteDiagnosis[]) => void;
  onClose: () => void;
}) {
  const uid = useId();
  const [rows, setRows] = useState<DxRow[]>(() => pickerRows(planDx, value));
  const [pick, setPick] = useState<DxPick>(() => initialPick(value));
  const [query, setQuery] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [err, setErr] = useState('');

  const selected = selectedRows(rows, pick.ticked);
  const others = otherRows(rows, pick.ticked, query);
  const typed = query.trim().toUpperCase();
  const canAdd = !others.length && isIcd10(typed) && !rows.some((r) => r.code === typed);

  const tick = (code: string, on: boolean) => {
    setPick((p) => tickCode(p, rows, code, on));
    setErr('');
  };
  const add = () => {
    const e = addError(typed, newLabel, rows);
    if (e) {
      setErr(e);
      return;
    }
    setRows((r) => [...r, { code: typed, label: newLabel.trim().slice(0, 120), source: 'search' }]);
    setPick((p) => tickCode(p, rows, typed, true));
    setQuery('');
    setNewLabel('');
    setErr('');
  };
  const save = () => {
    const next = savedDiagnoses(rows, new Set(pick.ticked), pick.primary);
    if (!next) {
      setErr('Pick at least one diagnosis.');
      return;
    }
    onSave(next);
  };

  const row = (r: DxRow) => {
    const on = pick.ticked.includes(r.code);
    const id = `${uid}-${r.code}`;
    return (
      <div key={r.code} className={`pn-dxp-row${on ? ' on' : ''}`} data-dx-row={r.code}>
        <input
          id={id}
          type="checkbox"
          checked={on}
          onChange={(e) => tick(r.code, e.target.checked)}
        />
        <label htmlFor={id} className="pn-dxp-code" data-dx-code="">
          {r.code}
        </label>
        <label htmlFor={id} className="pn-dxp-label">
          {r.label}
        </label>
        <span className="pn-dxp-prim">
          <input
            type="radio"
            name={`${uid}-primary`}
            checked={on && pick.primary === r.code}
            disabled={!on}
            aria-label={`Primary: ${r.code}`}
            data-dx-primary=""
            onChange={() => setPick((p) => setPrimary(p, r.code))}
          />
        </span>
      </div>
    );
  };

  return (
    <Modal
      title="Diagnosis"
      onClose={onClose}
      className="pn-dxc"
      hooks={{ root: 'data-dx-picker', close: 'data-dx-close', foot: 'data-dx-footer' }}
      actions={
        <>
          {err && (
            <p className="pn-dxc-err" role="alert">
              {err}
            </p>
          )}
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="pn-dxp-top">
        <div className="pn-dxp-search">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            type="search"
            placeholder="Search code or name (F43, dementia)"
            aria-label="Search diagnoses"
            autoComplete="off"
            value={query}
            data-dx-search=""
            onChange={(e) => {
              setQuery(e.target.value);
              setErr('');
            }}
          />
        </div>
        <div className="pn-dxp-head" data-dx-header="">
          <span>Code</span>
          <span>Diagnosis</span>
          <span>Primary</span>
        </div>
      </div>
      <section className="pn-dxp-sec" data-dx-selected="">
        <h4 className="pn-dxp-cap sel">
          Selected (<span data-dx-selected-count="">{selected.length}</span>)
        </h4>
        {selected.map(row)}
      </section>
      <section className="pn-dxp-sec" data-dx-all="">
        <h4 className="pn-dxp-cap">All diagnoses</h4>
        {others.length ? (
          others.map(row)
        ) : (
          <div className="pn-dxp-none">
            <p>No code matches &ldquo;{query.trim()}&rdquo;.</p>
            {canAdd && (
              <div className="pn-dxp-add">
                <b>{typed}</b>
                <input
                  className="pn-chip-other"
                  placeholder="Description"
                  aria-label="Diagnosis description"
                  autoComplete="off"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      add();
                    }
                  }}
                />
                <button type="button" className="btn" onClick={add}>
                  Add
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </Modal>
  );
}
